import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { Link } from "wouter";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Clock,
  ShieldAlert,
  TrendingDown,
  TrendingUp,
  FileText,
  Calendar,
  CalendarClock,
  RefreshCw,
  AlertCircle,
  ArrowRight,
  Users,
  Building,
  HardDrive,
  Settings,
  ClipboardCheck,
  CheckCircle,
  Timer,
  Activity,
  Target,
  BarChart3,
  ChevronRight,
  ChevronDown,
  Zap,
  BookOpen,
  Inbox,
  ClipboardList,
  Lightbulb,
  DollarSign,
  Kanban,
  CreditCard,
  Plug,
  Shield,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import { getUserPermissions } from "@/lib/rbac";
import { isVcioPlus } from "@/lib/reviewWorkflow/gating";
import { ReviewsDueQueue } from "@/components/reviews/ReviewsDueQueue";
import { pendingReviewLinkTarget } from "@shared/pendingReviewLink";
import { useUserTimezone } from "@/hooks/useUserTimezone";
import { MfaNudge } from "@/components/ui/mfa-nudge";
import { BillingNudge } from "@/components/ui/billing-nudge";
import { FirstTimeSetupChecklist } from "@/components/ui/first-time-setup-checklist";

const PERIOD_OPTIONS = [
  { value: "last_30_days", label: "Last 30 days" },
  { value: "last_quarter", label: "This quarter" },
  { value: "last_6_months", label: "Last 6 months" },
  { value: "last_year", label: "Last 12 months" },
];

// ---- helpers --------------------------------------------------------------

function greeting(firstName: string) {
  const hour = new Date().getHours();
  if (hour < 12) return `Good morning, ${firstName}`;
  if (hour < 17) return `Good afternoon, ${firstName}`;
  return `Good evening, ${firstName}`;
}

function todayLong() {
  return new Date().toLocaleDateString("en-US", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

function initials(name: string) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  return (parts[0]?.[0] || "") + (parts[1]?.[0] || "");
}

function daysAgo(date: any): number | null {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / (1000 * 60 * 60 * 24));
}

const AVATAR_COLORS = [
  "bg-blue-500/20 text-blue-400",
  "bg-emerald-500/20 text-emerald-400",
  "bg-purple-500/20 text-purple-400",
  "bg-amber-500/20 text-amber-400",
  "bg-rose-500/20 text-rose-400",
  "bg-cyan-500/20 text-cyan-400",
];
function avatarColor(seed: number) {
  return AVATAR_COLORS[Math.abs(seed) % AVATAR_COLORS.length];
}

function scoreColor(score: number | null) {
  if (score === null) return "text-muted-foreground";
  if (score >= 80) return "text-emerald-400";
  if (score >= 60) return "text-blue-400";
  if (score >= 40) return "text-amber-400";
  return "text-rose-400";
}
function scoreBarColor(score: number | null) {
  if (score === null) return "bg-muted-foreground/40";
  if (score >= 80) return "bg-emerald-500";
  if (score >= 60) return "bg-blue-500";
  if (score >= 40) return "bg-amber-500";
  return "bg-rose-500";
}

// ---- small building blocks ------------------------------------------------

function Sparkline({ data, color }: { data: number[]; color: string }) {
  if (!data || data.length < 2) {
    return <div className="h-9" />;
  }
  const chartData = data.map((v, i) => ({ i, v }));
  return (
    <div className="h-9 w-full" data-testid="stat-sparkline">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={chartData} margin={{ top: 4, bottom: 4, left: 0, right: 0 }}>
          <Line
            type="monotone"
            dataKey="v"
            stroke={color}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

function DeltaBadge({
  delta,
  unit = "",
  invert = false,
}: {
  delta: number | null;
  unit?: string;
  invert?: boolean;
}) {
  if (delta === null || delta === undefined) {
    return (
      <span className="text-[11px] text-muted-foreground">Building history</span>
    );
  }
  if (delta === 0) {
    return <span className="text-[11px] text-muted-foreground">No change</span>;
  }
  const up = delta > 0;
  // For most metrics up is good; for "invert" metrics (at-risk) up is bad.
  const good = invert ? !up : up;
  const Icon = up ? TrendingUp : TrendingDown;
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] font-medium ${
        good ? "text-emerald-400" : "text-rose-400"
      }`}
    >
      <Icon className="h-3 w-3" />
      {Math.abs(delta)}
      {unit ? ` ${unit}` : ""}
    </span>
  );
}

interface StatCardProps {
  label: string;
  value: string;
  delta?: number | null;
  deltaUnit?: string;
  invertDelta?: boolean;
  sparkline?: number[];
  sparkColor?: string;
  footer?: React.ReactNode;
  badge?: { text: string; tone: "warning" | "info" };
  progress?: { value: number; color: string };
  href: string;
  testId?: string;
}

function StatCard(props: StatCardProps) {
  return (
    <Link href={props.href}>
      <Card
        className="cursor-pointer transition-colors hover:bg-muted/40 h-full"
        data-testid={props.testId}
      >
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <span className="text-xs text-muted-foreground">{props.label}</span>
            {props.badge && (
              <Badge
                variant="outline"
                className={
                  props.badge.tone === "warning"
                    ? "border-amber-500/40 text-amber-400 text-[10px]"
                    : "border-blue-500/40 text-blue-400 text-[10px]"
                }
              >
                {props.badge.text}
              </Badge>
            )}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span className="text-2xl font-bold tracking-tight">
              {props.value}
            </span>
            {props.delta !== undefined && (
              <DeltaBadge
                delta={props.delta}
                unit={props.deltaUnit}
                invert={props.invertDelta}
              />
            )}
          </div>
          {props.sparkline && (
            <div className="mt-2">
              <Sparkline
                data={props.sparkline}
                color={props.sparkColor || "#3b82f6"}
              />
            </div>
          )}
          {props.progress && (
            <div className="mt-3">
              <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${Math.min(100, Math.max(0, props.progress.value))}%`,
                    backgroundColor: props.progress.color,
                  }}
                />
              </div>
            </div>
          )}
          {props.footer && (
            <div className="mt-2 text-[11px] text-muted-foreground">
              {props.footer}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
}

const SEVERITY_STYLE: Record<string, string> = {
  critical: "text-rose-400 bg-rose-500/10",
  warning: "text-amber-400 bg-amber-500/10",
  info: "text-blue-400 bg-blue-500/10",
};

function feedIcon(name: string) {
  switch (name) {
    case "clock":
      return Clock;
    case "shield":
      return ShieldAlert;
    case "alert":
      return AlertCircle;
    case "calendar":
      return Calendar;
    case "refresh":
      return RefreshCw;
    case "timer":
      return Timer;
    default:
      return FileText;
  }
}

function actionLabel(icon: string) {
  switch (icon) {
    case "clock":
      return "Review";
    case "shield":
      return "View assets";
    case "calendar":
      return "Prepare";
    default:
      return "View";
  }
}

function NeedsAttentionPanel({ items }: { items: any[] }) {
  return (
    <Card className="h-full">
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-sm font-semibold">
          <AlertCircle className="h-4 w-4 text-rose-400" />
          Needs your attention
          {items.length > 0 && (
            <Badge variant="secondary" className="text-[10px]">
              {items.length} item{items.length === 1 ? "" : "s"}
            </Badge>
          )}
        </CardTitle>
        <Link href="/assessments/technical-alignment?filter=pending_review">
          <span className="text-xs text-blue-400 hover:underline">View all</span>
        </Link>
      </CardHeader>
      <CardContent>
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="h-8 w-8 text-emerald-400 mb-2" />
            <p className="text-sm text-muted-foreground">
              You're all caught up. Nothing needs attention right now.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => {
              const Icon = feedIcon(item.icon);
              return (
                <div
                  key={item.id}
                  className="flex items-center justify-between gap-3 rounded-lg border border-border/60 p-3"
                  data-testid={`attention-${item.id}`}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div
                      className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${
                        SEVERITY_STYLE[item.severity] || SEVERITY_STYLE.info
                      }`}
                    >
                      <Icon className="h-4 w-4" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {item.detail}
                      </p>
                    </div>
                  </div>
                  <Link href={item.href}>
                    <Button size="sm" variant="secondary" className="shrink-0">
                      {actionLabel(item.icon)}
                    </Button>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function dateChip(dateStr: string | null) {
  if (!dateStr) return { top: "SOON", bottom: "" };
  const d = new Date(dateStr);
  const today = new Date();
  const isToday =
    d.getFullYear() === today.getFullYear() &&
    d.getMonth() === today.getMonth() &&
    d.getDate() === today.getDate();
  return {
    top: isToday
      ? "TODAY"
      : d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase(),
    bottom: String(d.getDate()),
  };
}

function UpNextPanel({ items }: { items: any[] }) {
  return (
    <Card className="h-full">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <CalendarClock className="h-4 w-4 text-blue-400" />
            <span className="font-semibold text-sm">Up next</span>
          </div>
          <Link href="/calendar">
            <span className="text-xs text-blue-400 hover:underline">Calendar</span>
          </Link>
        </div>
        {items.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <Calendar className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              Nothing scheduled. Upcoming reviews and meetings show up here.
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {items.map((item) => {
              const chip = dateChip(item.date);
              return (
                <Link key={item.id} href={item.href}>
                  <div
                    className="flex items-center gap-3 rounded-lg p-2 hover:bg-muted/40 transition-colors cursor-pointer"
                    data-testid={`upnext-${item.id}`}
                  >
                    <div className="flex h-11 w-11 shrink-0 flex-col items-center justify-center rounded-md bg-muted/60">
                      <span className="text-[9px] font-semibold text-muted-foreground leading-none">
                        {chip.top}
                      </span>
                      {chip.bottom && (
                        <span className="text-base font-bold leading-tight">
                          {chip.bottom}
                        </span>
                      )}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{item.title}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {item.detail}
                      </p>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PerformanceChart({
  history,
  avg,
  delta,
}: {
  history: any;
  avg: number | null;
  delta: number | null;
}) {
  const points = (history?.points || []).map((p: any) => ({
    label: p.label,
    score: p.score,
  }));
  const hasData = history?.status !== "insufficient";
  const numeric = points
    .filter((p: any) => p.score !== null)
    .map((p: any) => p.score);
  const min = numeric.length ? Math.max(0, Math.min(...numeric) - 5) : 0;
  const max = numeric.length ? Math.min(100, Math.max(...numeric) + 5) : 100;

  return (
    <Card className="h-full">
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-2">
          <div>
            <p className="text-xs text-muted-foreground">
              Average alignment — last 6 months
            </p>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-2xl font-bold">
                {avg !== null ? `${avg}%` : "—"}
              </span>
              <DeltaBadge delta={delta} unit="pts" />
            </div>
          </div>
          {history?.status === "partial" && (
            <Badge
              variant="outline"
              className="border-amber-500/40 text-amber-400 text-[10px]"
            >
              Building history
            </Badge>
          )}
        </div>
        <div className="h-[240px] mt-3 relative">
          {!hasData && (
            <div className="absolute inset-0 z-10 flex flex-col items-center justify-center text-center px-6">
              <Activity className="h-7 w-7 text-muted-foreground mb-2" />
              <p className="text-sm font-medium">Building your history</p>
              <p className="text-xs text-muted-foreground max-w-xs">
                Trends fill in as alignment scores are recorded over time and as
                business reviews are completed.
              </p>
            </div>
          )}
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart
              data={points}
              margin={{ top: 10, right: 10, left: -16, bottom: 0 }}
            >
              <defs>
                <linearGradient id="alignFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.35} />
                  <stop offset="100%" stopColor="#3b82f6" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                stroke="currentColor"
                className="text-muted/30"
                vertical={false}
              />
              <XAxis
                dataKey="label"
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: "currentColor" }}
                className="text-muted-foreground"
              />
              <YAxis
                domain={[min, max]}
                tickLine={false}
                axisLine={false}
                tick={{ fontSize: 12, fill: "currentColor" }}
                className="text-muted-foreground"
                width={36}
              />
              <Tooltip
                contentStyle={{
                  background: "hsl(var(--popover))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: 8,
                  fontSize: 12,
                }}
                formatter={(v: any) => [v === null ? "No data" : `${v}%`, "Alignment"]}
              />
              <Area
                type="monotone"
                dataKey="score"
                stroke="#3b82f6"
                strokeWidth={2.5}
                fill="url(#alignFill)"
                connectNulls
                dot={{ r: 4, fill: "#3b82f6", strokeWidth: 0 }}
                activeDot={{ r: 5 }}
                isAnimationActive={!!hasData}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}

function ScoreDistributionPanel({ dist }: { dist: any }) {
  const bands = [
    { key: "excellent", label: "Excellent", range: "80-100", color: "#22c55e", dot: "bg-emerald-500" },
    { key: "healthy", label: "Healthy", range: "60-79", color: "#3b82f6", dot: "bg-blue-500" },
    { key: "needsAttention", label: "Needs attention", range: "40-59", color: "#f59e0b", dot: "bg-amber-500" },
    { key: "atRisk", label: "At risk", range: "below 40", color: "#ef4444", dot: "bg-rose-500" },
  ];
  const assessed =
    dist.excellent + dist.healthy + dist.needsAttention + dist.atRisk;
  const healthyOrBetter =
    assessed > 0
      ? Math.round(((dist.excellent + dist.healthy) / assessed) * 100)
      : null;

  return (
    <Card className="h-full">
      <CardContent className="p-5">
        <div className="mb-4">
          <p className="font-semibold text-sm">Score distribution</p>
          <p className="text-xs text-muted-foreground">By alignment band</p>
        </div>
        {assessed === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <Inbox className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">
              No assessed customers yet. Approve an assessment to see the
              distribution.
            </p>
          </div>
        ) : (
          <>
            <div className="flex h-2.5 w-full overflow-hidden rounded-full mb-5">
              {bands.map((b) =>
                dist[b.key] > 0 ? (
                  <div
                    key={b.key}
                    style={{
                      width: `${(dist[b.key] / assessed) * 100}%`,
                      backgroundColor: b.color,
                    }}
                  />
                ) : null,
              )}
            </div>
            <div className="space-y-3">
              {bands.map((b) => (
                <div key={b.key} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${b.dot}`} />
                    <span className="text-sm">{b.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {b.range}
                    </span>
                  </div>
                  <span className="text-sm font-semibold">{dist[b.key]}</span>
                </div>
              ))}
              {dist.notAssessed > 0 && (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-2.5 w-2.5 rounded-full bg-muted-foreground/40" />
                    <span className="text-sm text-muted-foreground">
                      Not assessed
                    </span>
                  </div>
                  <span className="text-sm font-semibold text-muted-foreground">
                    {dist.notAssessed}
                  </span>
                </div>
              )}
            </div>
            <div className="mt-5 flex items-center justify-between border-t border-border/60 pt-4">
              <span className="text-xs text-muted-foreground">
                {healthyOrBetter}% healthy or better
              </span>
              <Link href="/customers">
                <span className="text-xs text-blue-400 hover:underline inline-flex items-center gap-1">
                  All customers <ArrowRight className="h-3 w-3" />
                </span>
              </Link>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

function CustomersAttentionTable({ rows }: { rows: any[] }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-sm">
              Customers needing attention
            </span>
            {rows.length > 0 && (
              <Badge variant="secondary" className="text-[10px]">
                {rows.length} flagged
              </Badge>
            )}
          </div>
          <Link href="/customers">
            <span className="text-xs text-blue-400 hover:underline">
              View report
            </span>
          </Link>
        </div>
        {rows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-10 text-center">
            <CheckCircle className="h-8 w-8 text-emerald-400 mb-2" />
            <p className="text-sm text-muted-foreground">
              Every customer is tracking well right now.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-muted-foreground">
                  <th className="text-left font-medium pb-2">Customer</th>
                  <th className="text-left font-medium pb-2 hidden md:table-cell">
                    Industry
                  </th>
                  <th className="text-left font-medium pb-2">Score</th>
                  <th className="text-left font-medium pb-2 hidden sm:table-cell">
                    30-day
                  </th>
                  <th className="text-left font-medium pb-2 hidden lg:table-cell">
                    Last review
                  </th>
                  <th className="text-right font-medium pb-2"></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => {
                  const reviewDays = daysAgo(c.lastReviewDate);
                  return (
                    <tr
                      key={c.id}
                      className="border-t border-border/50"
                      data-testid={`attention-row-${c.id}`}
                    >
                      <td className="py-3">
                        <div className="flex items-center gap-3">
                          <div
                            className={`flex h-8 w-8 items-center justify-center rounded-full text-xs font-semibold uppercase ${avatarColor(
                              c.id,
                            )}`}
                          >
                            {initials(c.name)}
                          </div>
                          <span className="font-medium truncate max-w-[160px]">
                            {c.name}
                          </span>
                        </div>
                      </td>
                      <td className="py-3 text-muted-foreground hidden md:table-cell">
                        {c.industry || "—"}
                      </td>
                      <td className="py-3">
                        {c.score === null ? (
                          <span className="text-xs text-muted-foreground">
                            Not assessed
                          </span>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span
                              className={`font-semibold ${scoreColor(c.score)}`}
                            >
                              {c.score}
                            </span>
                            <div className="h-1.5 w-14 rounded-full bg-muted overflow-hidden hidden sm:block">
                              <div
                                className={`h-full rounded-full ${scoreBarColor(
                                  c.score,
                                )}`}
                                style={{ width: `${c.score}%` }}
                              />
                            </div>
                          </div>
                        )}
                      </td>
                      <td className="py-3 hidden sm:table-cell">
                        {c.delta30 === null ? (
                          <span className="text-xs text-muted-foreground">
                            New
                          </span>
                        ) : c.delta30 === 0 ? (
                          <span className="text-xs text-muted-foreground">
                            0
                          </span>
                        ) : (
                          <span
                            className={`inline-flex items-center gap-1 text-xs font-medium ${
                              c.delta30 > 0 ? "text-emerald-400" : "text-rose-400"
                            }`}
                          >
                            {c.delta30 > 0 ? (
                              <TrendingUp className="h-3 w-3" />
                            ) : (
                              <TrendingDown className="h-3 w-3" />
                            )}
                            {Math.abs(c.delta30)}
                          </span>
                        )}
                      </td>
                      <td className="py-3 text-muted-foreground hidden lg:table-cell">
                        {reviewDays === null
                          ? "No review yet"
                          : reviewDays === 0
                            ? "Today"
                            : `${reviewDays} day${reviewDays === 1 ? "" : "s"} ago`}
                      </td>
                      <td className="py-3 text-right">
                        <Link href={`/customers/${c.id}`}>
                          <Button
                            size="sm"
                            variant={
                              c.score !== null && c.score < 40
                                ? "default"
                                : "secondary"
                            }
                          >
                            {c.overdue || (c.score !== null && c.score < 40)
                              ? "Review"
                              : "Open"}
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function PipelinePanel({ pipeline }: { pipeline: any }) {
  const total =
    (pipeline?.inProgress || 0) +
    (pipeline?.pendingReview || 0) +
    (pipeline?.approvedThisMonth || 0);
  const seg = (n: number) => (total > 0 ? (n / total) * 100 : 0);
  return (
    <Card className="h-full">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <span className="font-semibold text-sm">Assessment pipeline</span>
          <span className="text-xs text-muted-foreground">{total} total</span>
        </div>
        <div className="flex h-2 w-full overflow-hidden rounded-full bg-muted mb-4">
          <div
            className="bg-blue-500"
            style={{ width: `${seg(pipeline?.inProgress || 0)}%` }}
          />
          <div
            className="bg-amber-500"
            style={{ width: `${seg(pipeline?.pendingReview || 0)}%` }}
          />
          <div
            className="bg-emerald-500"
            style={{ width: `${seg(pipeline?.approvedThisMonth || 0)}%` }}
          />
        </div>
        <div className="space-y-2.5">
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-blue-500" /> In progress
            </span>
            <span className="font-semibold">{pipeline?.inProgress || 0}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-amber-500" /> Pending review
            </span>
            <span className="font-semibold">{pipeline?.pendingReview || 0}</span>
          </div>
          <div className="flex items-center justify-between text-sm">
            <span className="flex items-center gap-2">
              <span className="h-2 w-2 rounded-full bg-emerald-500" /> Approved this
              month
            </span>
            <span className="font-semibold">
              {pipeline?.approvedThisMonth || 0}
            </span>
          </div>
        </div>
        <Link href="/assessments/technical-alignment">
          <Button variant="secondary" className="w-full mt-4">
            Go to assessments
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function AssetHealthPanel({ assets }: { assets: any }) {
  const total = assets?.totalAssets || 0;
  const expired = assets?.warranty?.expired || 0;
  const expiring = assets?.warranty?.expiring30 || 0;
  const aging =
    (assets?.byLifecycleStage?.aging || 0) +
    (assets?.byLifecycleStage?.end_of_life || 0);
  const healthy = Math.max(0, total - expired - expiring - aging);
  const pct = total > 0 ? Math.round((healthy / total) * 100) : 0;
  const pieData = [
    { name: "Healthy", value: healthy, color: "#22c55e" },
    {
      name: "Other",
      value: Math.max(0, total - healthy),
      color: "#3f3f46",
    },
  ];

  return (
    <Card className="h-full">
      <CardContent className="p-5">
        <div className="mb-4">
          <span className="font-semibold text-sm">Asset &amp; warranty health</span>
        </div>
        {total === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <HardDrive className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground">No assets tracked yet.</p>
          </div>
        ) : (
          <div className="flex items-center gap-4">
            <div className="relative h-[110px] w-[110px] shrink-0">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={pieData}
                    dataKey="value"
                    innerRadius={40}
                    outerRadius={52}
                    startAngle={90}
                    endAngle={-270}
                    stroke="none"
                  >
                    {pieData.map((entry, i) => (
                      <Cell key={i} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <span className="text-lg font-bold">{pct}%</span>
                <span className="text-[10px] text-muted-foreground">healthy</span>
              </div>
            </div>
            <div className="flex-1 space-y-2 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Total assets</span>
                <span className="font-semibold">{total}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Expiring soon</span>
                <span className="font-semibold text-amber-400">{expiring}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Expired</span>
                <span className="font-semibold text-rose-400">{expired}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Aging</span>
                <span className="font-semibold">{aging}</span>
              </div>
            </div>
          </div>
        )}
        <Link href="/assets">
          <Button variant="secondary" className="w-full mt-4">
            Manage assets
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

function integrationStatusLabel(status: string) {
  switch (status) {
    case "active":
    case "connected":
      return { label: "Connected", className: "text-emerald-400", dot: "bg-emerald-500" };
    case "error":
      return { label: "Reconnect", className: "text-rose-400", dot: "bg-rose-500" };
    default:
      return { label: "Not set up", className: "text-muted-foreground", dot: "bg-muted-foreground/40" };
  }
}

function IntegrationsPanel({ integrations, health }: { integrations: any[]; health: any }) {
  return (
    <Card className="h-full">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <span className="font-semibold text-sm">Integrations</span>
          {health && (
            <Badge variant="secondary" className="text-[10px]">
              {health.connected}/{health.total}
            </Badge>
          )}
        </div>
        {(!integrations || integrations.length === 0) ? (
          <div className="flex flex-col items-center justify-center py-6 text-center">
            <Zap className="h-8 w-8 text-muted-foreground mb-2" />
            <p className="text-sm text-muted-foreground mb-3">
              No integrations connected yet.
            </p>
            <Link href="/integrations">
              <Button size="sm" variant="secondary">
                Set up integrations
              </Button>
            </Link>
          </div>
        ) : (
          <div className="space-y-2.5">
            {integrations.map((it) => {
              const s = integrationStatusLabel(it.status);
              return (
                <div
                  key={it.id}
                  className="flex items-center justify-between"
                  data-testid={`integration-${it.id}`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                    <span className="text-sm">{it.name}</span>
                  </div>
                  <span className={`text-xs font-medium ${s.className}`}>
                    {s.label}
                  </span>
                </div>
              );
            })}
            <Link href="/integrations">
              <span className="mt-2 inline-flex items-center gap-1 text-xs text-blue-400 hover:underline">
                Manage integrations <ChevronRight className="h-3 w-3" />
              </span>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ---- loading skeleton -----------------------------------------------------

function DashboardSkeleton() {
  return (
    <div className="space-y-4 p-1">
      <div className="h-8 w-64 bg-muted rounded animate-pulse" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="h-48 bg-muted rounded-lg animate-pulse" />
        <div className="h-48 bg-muted rounded-lg animate-pulse" />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} className="h-28 bg-muted rounded-lg animate-pulse" />
        ))}
      </div>
      <div className="h-72 bg-muted rounded-lg animate-pulse" />
    </div>
  );
}

// ---- manager (vcio / admin / owner) view ----------------------------------

// ---- vCIO delivery pipeline (connected sequence) --------------------------
// Surfaces the vCIO workflow — review → recommendations → budget → business
// review → present — as one connected, navigable sequence linking each stage to
// its workspace.
function DeliveryPipeline() {
  // Counts intentionally live in the KPI stat cards / Assessment pipeline panel
  // below — this panel's job is to show the flow as a connected sequence, so it
  // stays navigation-only to avoid duplicating those numbers.
  const steps = [
    {
      n: 1,
      title: "Review Schedule",
      href: "/review-schedule",
      icon: ClipboardList,
      desc: "Run recurring alignment reviews",
    },
    {
      n: 2,
      title: "Recommendations",
      href: "/recommendations",
      icon: Lightbulb,
      desc: "Turn gaps into prioritized actions",
    },
    {
      n: 3,
      title: "Budget Planning",
      href: "/budget-planning",
      icon: DollarSign,
      desc: "Plan spend for approved work",
    },
    {
      n: 4,
      title: "Business Reviews",
      href: "/qbrs",
      icon: FileText,
      desc: "Package findings into a QBR",
    },
    {
      n: 5,
      title: "Implementation",
      href: "/implementation-tracking",
      icon: Kanban,
      desc: "Track delivery to completion",
    },
  ];

  return (
    <Card data-testid="delivery-pipeline">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-1">
          <ArrowRight className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold text-sm">Delivery pipeline</span>
        </div>
        <p className="text-xs text-muted-foreground mb-4">
          From review to presented — your vCIO delivery flow, end to end.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3 lg:gap-x-7 lg:gap-y-3">
          {steps.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={s.href} className="relative">
                <Link href={s.href}>
                  <div
                    className="group h-full rounded-lg border border-border/60 bg-muted/30 p-3 hover:border-primary/50 hover:bg-muted/60 transition-colors cursor-pointer"
                    data-testid={`pipeline-step-${s.n}`}
                  >
                    <div className="flex items-center justify-between mb-2">
                      <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-bold">
                        {s.n}
                      </span>
                      <Icon className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                    </div>
                    <div className="text-sm font-medium leading-tight">{s.title}</div>
                    <div className="mt-1 text-xs text-muted-foreground">{s.desc}</div>
                  </div>
                </Link>
                {i < steps.length - 1 && (
                  <ArrowRight className="hidden lg:block absolute top-1/2 -right-[22px] -translate-y-1/2 h-4 w-4 text-muted-foreground/60 z-10" />
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ---- admin/owner workspace setup & health ---------------------------------
function WorkspaceHealthPanel({ data }: { data: any }) {
  const health = data.stats?.integrationHealth;
  const disconnected = health
    ? Math.max(0, (health.total || 0) - (health.connected || 0))
    : 0;
  // The integration count itself lives in the KPI stat card + Integrations panel
  // below; here we surface an actionable status instead so nothing is repeated.
  const tiles = [
    {
      title: "Integrations",
      href: "/integrations",
      icon: Plug,
      hint: !health
        ? "Connect your tools"
        : disconnected === 0
          ? "All connected"
          : `${disconnected} need attention`,
      tone: !health ? "muted" : disconnected === 0 ? "ok" : "warn",
    },
    {
      title: "Team & access",
      href: "/users",
      icon: Users,
      hint: "Manage members & roles",
      tone: "muted",
    },
    {
      title: "Security & audit",
      href: "/audit-logs",
      icon: Shield,
      hint: "Review activity log",
      tone: "muted",
    },
    {
      title: "Configuration",
      href: "/settings",
      icon: Settings,
      hint: "Org & security settings",
      tone: "muted",
    },
  ];

  return (
    <Card data-testid="workspace-health">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <Settings className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold text-sm">Workspace setup &amp; health</span>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
          {tiles.map((t) => {
            const Icon = t.icon;
            return (
              <Link key={t.href} href={t.href}>
                <div className="h-full rounded-lg border border-border/60 bg-muted/30 p-3 hover:border-primary/50 hover:bg-muted/60 transition-colors cursor-pointer">
                  <Icon className="h-4 w-4 text-muted-foreground mb-2" />
                  <div className="text-sm font-medium leading-tight">{t.title}</div>
                  <div
                    className={cn(
                      "mt-1 text-xs",
                      t.tone === "warn"
                        ? "text-amber-400"
                        : t.tone === "ok"
                          ? "text-emerald-400"
                          : "text-muted-foreground",
                    )}
                  >
                    {t.hint}
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

// ---- owner business & billing snapshot ------------------------------------
function BusinessBillingPanel({ organization }: { organization: any }) {
  // Owner-only billing emphasis. Business KPIs (customers, alignment, reviews)
  // are deliberately left to the shared stat cards below to avoid duplication;
  // this panel surfaces the one thing unique to the owner: subscription state.
  const tier = organization?.subscriptionTier
    ? String(organization.subscriptionTier)
    : null;
  const status = organization?.subscriptionStatus
    ? String(organization.subscriptionStatus)
    : null;
  const renews = organization?.subscriptionExpiresAt
    ? new Date(organization.subscriptionExpiresAt)
    : null;
  const statusOk = !status || status.toLowerCase() === "active";

  return (
    <Card data-testid="business-billing">
      <CardContent className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold text-sm">Subscription &amp; billing</span>
        </div>

        <Link href="/billing">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border/60 bg-muted/30 p-4 hover:border-primary/50 hover:bg-muted/60 transition-colors cursor-pointer">
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
              <div>
                <div className="text-xs text-muted-foreground">Current plan</div>
                <div className="text-base font-semibold capitalize">
                  {tier || "Subscription"}
                </div>
              </div>
              <Badge
                variant={statusOk ? "default" : "secondary"}
                className={cn(
                  !statusOk && "bg-amber-500/15 text-amber-500 hover:bg-amber-500/15",
                )}
              >
                {status ? status.replace(/_/g, " ") : "—"}
              </Badge>
              {renews && !Number.isNaN(renews.getTime()) && (
                <span className="text-xs text-muted-foreground">
                  Renews {renews.toLocaleDateString()}
                </span>
              )}
            </div>
            <span className="inline-flex items-center gap-1 text-xs text-blue-400 hover:underline shrink-0">
              Manage subscription <ChevronRight className="h-3 w-3" />
            </span>
          </div>
        </Link>
      </CardContent>
    </Card>
  );
}

function ManagerDashboard({
  data,
  permissions,
  user,
  period,
  setPeriod,
  organization,
}: any) {
  const role = (user as any)?.role;
  const isAdminPlus = role === "admin" || role === "owner";
  const isOwner = role === "owner";
  const stats = data.stats;
  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            {greeting((user as any)?.firstName || "there")}
            <span>👋</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            {isOwner
              ? "Business health, billing, and your team's delivery"
              : isAdminPlus
                ? "Workspace health, setup, and delivery across your team"
                : "Approvals, your delivery pipeline, and customer health"}{" "}
            — {todayLong()}.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-[150px]" data-testid="period-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {PERIOD_OPTIONS.map((o) => (
                <SelectItem key={o.value} value={o.value}>
                  {o.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="quick-actions-menu">
                <Zap className="h-4 w-4 mr-1.5" /> Quick actions
                <ChevronDown className="h-4 w-4 ml-1.5 opacity-70" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-52">
              <DropdownMenuLabel>Quick actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {permissions.canViewUsers && (
                <DropdownMenuItem asChild data-testid="action-manage-users">
                  <Link href="/users">
                    <Users className="h-4 w-4 mr-2" /> Manage users
                  </Link>
                </DropdownMenuItem>
              )}
              {permissions.canCreateCustomers && (
                <DropdownMenuItem asChild data-testid="action-add-customer">
                  <Link href="/customers">
                    <Plus className="h-4 w-4 mr-2" /> Add customer
                  </Link>
                </DropdownMenuItem>
              )}
              <DropdownMenuItem asChild data-testid="action-manage-assets">
                <Link href="/assets">
                  <HardDrive className="h-4 w-4 mr-2" /> Manage assets
                </Link>
              </DropdownMenuItem>
              {permissions.canManageSystem ? (
                <DropdownMenuItem asChild data-testid="action-org-settings">
                  <Link href="/settings">
                    <Settings className="h-4 w-4 mr-2" /> Organization settings
                  </Link>
                </DropdownMenuItem>
              ) : (
                <DropdownMenuItem asChild data-testid="action-business-reviews">
                  <Link href="/qbrs">
                    <FileText className="h-4 w-4 mr-2" /> Business reviews
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          <Link href="/assessments/technical-alignment">
            <Button data-testid="new-assessment">
              <Plus className="h-4 w-4 mr-1.5" /> New assessment
            </Button>
          </Link>
        </div>
      </div>

      <BillingNudge user={user} organization={organization} />
      <MfaNudge user={user} />
      <FirstTimeSetupChecklist />

      {/* Feeds — action items lead so the page opens on what needs doing today. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <NeedsAttentionPanel items={data.needsAttention || []} />
        <UpNextPanel items={data.upNext || []} />
      </div>

      {/* Reviews due work-queue — vCIO+ only (Req 2.1, 9.2). Immediately follows
          the Feeds row so it stays adjacent to NeedsAttentionPanel with no
          section interposed (Req 4.5). The cycle dueDate shown here is the
          same cadence-derived value rendered on the /quarterly-reviews list
          (Req 4.4). */}
      {isVcioPlus(user) && <ReviewsDueQueue />}

      {/* vCIO delivery pipeline as one connected sequence (vCIO+) — the flow map
          sits just under the reviews-due queue. */}
      <DeliveryPipeline />

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <StatCard
          label="Avg alignment score"
          value={stats.avgAlignment.value !== null ? `${stats.avgAlignment.value}%` : "—"}
          delta={stats.avgAlignment.delta}
          deltaUnit="pts"
          sparkline={stats.avgAlignment.sparkline}
          sparkColor="#22c55e"
          href="/customers"
          testId="stat-alignment"
        />
        <StatCard
          label="Active customers"
          value={String(stats.activeCustomers.value)}
          delta={stats.activeCustomers.delta}
          sparkColor="#3b82f6"
          href="/customers"
          testId="stat-customers"
        />
        <StatCard
          label="At-risk customers"
          value={String(stats.atRisk.value)}
          delta={stats.atRisk.delta}
          invertDelta
          sparkColor="#ef4444"
          href="/customers"
          testId="stat-at-risk"
        />
        <StatCard
          label="Pending reviews"
          value={String(stats.pendingReviews.value)}
          badge={
            stats.pendingReviews.value > 0
              ? { text: "Action", tone: "warning" }
              : undefined
          }
          footer={
            stats.pendingReviews.oldestWaitingDays !== null
              ? `Oldest waiting ${stats.pendingReviews.oldestWaitingDays} day${
                  stats.pendingReviews.oldestWaitingDays === 1 ? "" : "s"
                }`
              : "Nothing waiting"
          }
          href={pendingReviewLinkTarget(stats.pendingReviews.ids || [])}
          testId="stat-pending"
        />
        <StatCard
          label="Business reviews · QTD"
          value={String(stats.businessReviewsQtd.value)}
          delta={stats.businessReviewsQtd.delta}
          sparkColor="#14b8a6"
          href="/qbrs"
          testId="stat-brs"
        />
        {stats.integrationHealth ? (
          <StatCard
            label="Integration health"
            value={
              stats.integrationHealth.value !== null
                ? `${stats.integrationHealth.value}%`
                : "—"
            }
            progress={{
              value: stats.integrationHealth.value || 0,
              color: "#f59e0b",
            }}
            footer={`${stats.integrationHealth.connected} of ${stats.integrationHealth.total} connected`}
            href="/integrations"
            testId="stat-integrations"
          />
        ) : (
          <StatCard
            label="Customer base"
            value={String(data.scoreDistribution.total)}
            footer="Total customers"
            href="/customers"
            testId="stat-customer-base"
          />
        )}
      </div>

      {/* Performance + distribution */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <BarChart3 className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold text-sm">Customer base performance</span>
          <span className="text-xs text-muted-foreground">
            {data.scoreDistribution.total} customers
          </span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <PerformanceChart
            history={data.alignmentHistory}
            avg={stats.avgAlignment.value}
            delta={stats.avgAlignment.delta}
          />
          <ScoreDistributionPanel dist={data.scoreDistribution} />
        </div>
      </div>

      {/* Customers needing attention */}
      <CustomersAttentionTable rows={data.customersNeedingAttention || []} />

      {/* Role-specific panels — moved below day-to-day delivery so the page
          leads with delivery work. Workspace setup & health, then billing. */}
      {isAdminPlus && <WorkspaceHealthPanel data={data} />}
      {isOwner && <BusinessBillingPanel organization={organization} />}

      {/* Operations */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Activity className="h-4 w-4 text-muted-foreground" />
          <span className="font-semibold text-sm">Operations</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          <PipelinePanel pipeline={data.pipeline} />
          <AssetHealthPanel assets={data.assets} />
          {permissions.canViewIntegrations ? (
            <IntegrationsPanel
              integrations={data.integrations}
              health={stats.integrationHealth}
            />
          ) : (
            <UpNextMini data={data} />
          )}
        </div>
      </div>

    </div>
  );
}

// For vcio (no integrations panel) — show a compact "up next" overflow instead.
function UpNextMini({ data }: { data: any }) {
  return (
    <Card className="h-full">
      <CardContent className="p-5">
        <div className="flex items-center justify-between mb-4">
          <span className="font-semibold text-sm">Recommendations</span>
          <Link href="/qbrs">
            <span className="text-xs text-blue-400 hover:underline">
              Business reviews
            </span>
          </Link>
        </div>
        <div className="space-y-3 text-sm">
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Customers</span>
            <span className="font-semibold">{data.scoreDistribution.total}</span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Healthy or better</span>
            <span className="font-semibold text-emerald-400">
              {data.scoreDistribution.excellent + data.scoreDistribution.healthy}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">Needs attention</span>
            <span className="font-semibold text-amber-400">
              {data.scoreDistribution.needsAttention}
            </span>
          </div>
          <div className="flex items-center justify-between">
            <span className="text-muted-foreground">At risk</span>
            <span className="font-semibold text-rose-400">
              {data.scoreDistribution.atRisk}
            </span>
          </div>
        </div>
        <Link href="/customers">
          <Button variant="secondary" className="w-full mt-4">
            View customers
          </Button>
        </Link>
      </CardContent>
    </Card>
  );
}

// ---- alignment engineer view ----------------------------------------------

function EngineerDashboard({ data, user }: any) {
  const stats = data.stats;
  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
            {greeting((user as any)?.firstName || "there")}
            <span>👋</span>
          </h1>
          <p className="text-sm text-muted-foreground">
            Here's your assessment workload — {todayLong()}.
          </p>
        </div>
        <Link href="/assessments/technical-alignment">
          <Button data-testid="new-assessment">
            <Plus className="h-4 w-4 mr-1.5" /> New assessment
          </Button>
        </Link>
      </div>

      <MfaNudge user={user} />
      <FirstTimeSetupChecklist />

      {/* Stat cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard
          label="My assessments"
          value={String(stats.myAssessments)}
          footer="Total assigned"
          href="/assessments/technical-alignment"
          testId="stat-my-assessments"
        />
        <StatCard
          label="In progress"
          value={String(stats.inProgress)}
          footer="Active now"
          href="/assessments/technical-alignment?filter=in_progress"
          testId="stat-in-progress"
        />
        <StatCard
          label="Pending review"
          value={String(stats.pendingReview)}
          footer="Awaiting vCIO"
          href="/assessments/technical-alignment?filter=pending_review"
          testId="stat-pending-review"
        />
        <StatCard
          label="Completed"
          value={String(stats.completed)}
          footer={`${stats.completionRate}% completion rate`}
          href="/assessments/technical-alignment?filter=approved"
          testId="stat-completed"
        />
      </div>

      {/* Feeds */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <NeedsAttentionPanel items={data.needsAttention || []} />
        <UpNextPanel items={data.upNext || []} />
      </div>

      {/* Performance + pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Target className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold text-sm">My performance</span>
            </div>
            <div className="mb-4">
              <div className="flex justify-between text-sm mb-2">
                <span>Completion rate</span>
                <span className="font-semibold">{stats.completionRate}%</span>
              </div>
              <Progress value={stats.completionRate} className="h-2" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="text-center rounded-lg bg-muted/40 p-3">
                <div className="text-lg font-bold text-emerald-400">
                  {stats.submittedThisMonth}
                </div>
                <p className="text-xs text-muted-foreground">Submitted</p>
                <p className="text-[10px] text-muted-foreground/70">This month</p>
              </div>
              <div className="text-center rounded-lg bg-muted/40 p-3">
                <div className="text-lg font-bold text-blue-400">
                  {stats.avgCompletionDays}
                </div>
                <p className="text-xs text-muted-foreground">Avg days</p>
                <p className="text-[10px] text-muted-foreground/70">To complete</p>
              </div>
              <div className="text-center rounded-lg bg-muted/40 p-3">
                <div className="text-lg font-bold text-purple-400">
                  {stats.totalQuestionsAnswered}
                </div>
                <p className="text-xs text-muted-foreground">Questions</p>
                <p className="text-[10px] text-muted-foreground/70">Answered</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <PipelinePanel pipeline={data.pipeline} />
      </div>

      {/* Recent work */}
      {data.recentWork && data.recentWork.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <div className="flex items-center gap-2 mb-4">
              <Activity className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold text-sm">My recent work</span>
            </div>
            <div className="space-y-1">
              {data.recentWork.map((item: any) => (
                <Link key={item.id} href={`/assessments/${item.id}`}>
                  <div className="flex items-center justify-between rounded-lg p-2 hover:bg-muted/40 transition-colors cursor-pointer">
                    <div className="flex items-center gap-3">
                      {item.status === "approved" && (
                        <CheckCircle className="h-4 w-4 text-emerald-400" />
                      )}
                      {item.status === "pending_review" && (
                        <Clock className="h-4 w-4 text-amber-400" />
                      )}
                      {item.status === "in_progress" && (
                        <Timer className="h-4 w-4 text-blue-400" />
                      )}
                      {item.status === "draft" && (
                        <FileText className="h-4 w-4 text-muted-foreground" />
                      )}
                      <span className="text-sm font-medium">
                        {item.customerName || "Unknown customer"}
                      </span>
                    </div>
                    <Badge
                      variant={
                        item.status === "approved" ? "default" : "secondary"
                      }
                    >
                      {item.status === "pending_review"
                        ? "Pending review"
                        : item.status === "in_progress"
                          ? "In progress"
                          : item.status === "approved"
                            ? "Approved"
                            : "Draft"}
                    </Badge>
                  </div>
                </Link>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Quick actions */}
      <div className="flex flex-wrap gap-2 border-t border-border/60 pt-4">
        <Link href="/assessments/technical-alignment">
          <Button variant="outline" size="sm">
            <Plus className="h-4 w-4 mr-1.5" /> New assessment
          </Button>
        </Link>
        <Link href="/customers">
          <Button variant="outline" size="sm">
            <Building className="h-4 w-4 mr-1.5" /> View customers
          </Button>
        </Link>
        <Link href="/templates">
          <Button variant="outline" size="sm">
            <BookOpen className="h-4 w-4 mr-1.5" /> Templates
          </Button>
        </Link>
      </div>
    </div>
  );
}

// ---- page -----------------------------------------------------------------

export default function Dashboard() {
  const { user } = useAuth();
  const permissions = getUserPermissions(user);
  useUserTimezone();
  const [period, setPeriod] = useState("last_30_days");
  const role = (user as any)?.role;

  const { data: organization } = useQuery<any>({
    queryKey: ["/api/organizations/current"],
    queryFn: () => apiRequest("GET", "/api/organizations/current"),
  });

  const { data: overview, isLoading } = useQuery<any>({
    queryKey: [`/api/dashboard/overview?period=${period}`],
    enabled: !!user,
  });

  if (!user || isLoading || !overview) {
    return <DashboardSkeleton />;
  }

  if (role === "alignment_engineer") {
    return <EngineerDashboard data={overview} user={user} />;
  }

  return (
    <ManagerDashboard
      data={overview}
      permissions={permissions}
      user={user}
      period={period}
      setPeriod={setPeriod}
      organization={organization}
    />
  );
}
